import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';

const LandingPage: React.FC = () => {
  const navigate = useNavigate();
  const auth = useAuth();

  const handleSignupClick = async () => {
    try {
      await auth.signinRedirect();
    } catch (error) {
      console.error('Erro ao redirecionar para login:', error);
    }
  };

  const handlePricingClick = () => {
    navigate('/precos');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-200">
      {/* Header */}
      <header className="bg-white/95 backdrop-blur-md border-b border-gray-200 sticky top-0 z-50">
        <div className="container mx-auto px-4">
          <div className="flex justify-between items-center py-4">
            <div className="text-blue-700 text-2xl font-bold">
              <h1>SinalInvest</h1>
            </div>
            
            <nav className="hidden md:flex items-center gap-8">
              <div className="flex gap-8 items-center">
                <a href="#sobre" className="text-gray-900 no-underline font-medium text-base transition-colors duration-200 relative py-2 after:content-[''] after:absolute after:bottom-0 after:left-0 after:w-0 after:h-0.5 after:bg-blue-700 after:transition-all after:duration-300 hover:text-blue-700 hover:after:w-full">Sobre</a>
                <a href="#como-funciona" className="text-gray-900 no-underline font-medium text-base transition-colors duration-200 relative py-2 after:content-[''] after:absolute after:bottom-0 after:left-0 after:w-0 after:h-0.5 after:bg-blue-700 after:transition-all after:duration-300 hover:text-blue-700 hover:after:w-full">Como Funciona</a>
                <a href="#precos" className="text-gray-900 no-underline font-medium text-base transition-colors duration-200 relative py-2 after:content-[''] after:absolute after:bottom-0 after:left-0 after:w-0 after:h-0.5 after:bg-blue-700 after:transition-all after:duration-300 hover:text-blue-700 hover:after:w-full" onClick={handlePricingClick}>Preços</a>
                <a href="#contato" className="text-gray-900 no-underline font-medium text-base transition-colors duration-200 relative py-2 after:content-[''] after:absolute after:bottom-0 after:left-0 after:w-0 after:h-0.5 after:bg-blue-700 after:transition-all after:duration-300 hover:text-blue-700 hover:after:w-full">Contato</a>
              </div>
              
              <div className="flex gap-4 items-center">
                <button className="px-6 py-2 rounded-lg font-medium text-base text-blue-700 bg-white border-2 border-blue-700 hover:bg-blue-700 hover:text-white transition-all duration-200" onClick={handleSignupClick}>
                  Entrar
                </button>
                <button className="px-6 py-2 rounded-lg font-medium text-base text-white bg-blue-700 border-2 border-blue-700 hover:bg-blue-800 transition-all duration-200" onClick={handleSignupClick}>
                  Começar Grátis
                </button>
              </div>
            </nav>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-blue-900 to-blue-800 text-white relative overflow-hidden">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-16 items-center relative z-10">
            <div className="animate-fadeIn">
              <h1 className="text-5xl font-bold leading-tight mb-6 bg-gradient-to-br from-white to-slate-200 bg-clip-text text-transparent">
                Transforme notícias em decisões inteligentes para suas ações!
              </h1>
              
              <p className="text-xl leading-relaxed mb-8 opacity-90">
                Receba análises instantâneas de notícias das principais empresas da bolsa, 
                <strong> diretamente no seu WhatsApp</strong> de maneira fácil e rápida, e saiba como elas podem impactar suas ações.
              </p>

              <div className="mb-12">
                <div className="flex items-start gap-4 mb-4 text-lg">
                  <span className="text-2xl mt-1">🔹</span>
                  <span>Inteligência artificial analisando milhares de notícias em segundos.</span>
                </div>
                <div className="flex items-start gap-4 mb-4 text-lg">
                  <span className="text-2xl mt-1">🔹</span>
                  <span><strong>Receba análises completas via WhatsApp</strong> - rápido, fácil e direto no seu celular!</span>
                </div>
                <div className="flex items-start gap-4 mb-4 text-lg">
                  <span className="text-2xl mt-1">🔹</span>
                  <span>Alertas precisos sobre impactos positivos, negativos ou neutros nas ações.</span>
                </div>
                <div className="flex items-start gap-4 mb-4 text-lg">
                  <span className="text-2xl mt-1">🔹</span>
                  <span>Personalize notificações para as empresas que você acompanha.</span>
                </div>
              </div>

              <div className="flex gap-4 flex-wrap">
                <button className="px-8 py-4 text-lg font-semibold rounded-lg text-white bg-blue-700 border-2 border-blue-700 hover:bg-blue-800 transition-all duration-200" onClick={handleSignupClick}>
                  Assine agora e domine o mercado!
                </button>
                <button className="px-8 py-4 text-lg font-semibold rounded-lg text-blue-700 bg-white border-2 border-blue-700 hover:bg-blue-700 hover:text-white transition-all duration-200" onClick={handlePricingClick}>
                  Ver Planos
                </button>
              </div>
            </div>

            <div className="flex justify-center items-center animate-slideIn">
              <div className="relative w-72 h-48 bg-white/10 rounded-xl p-8 backdrop-blur-md border border-white/20">
                <div className="flex items-end justify-between h-32 mb-4">
                  <div className="w-5 bg-gradient-to-br from-green-500 to-green-700 rounded-t-md animate-growUp" style={{ height: '60%', animationDelay: '0s' }}></div>
                  <div className="w-5 bg-gradient-to-br from-green-500 to-green-700 rounded-t-md animate-growUp" style={{ height: '80%', animationDelay: '0.2s' }}></div>
                  <div className="w-5 bg-gradient-to-br from-green-500 to-green-700 rounded-t-md animate-growUp" style={{ height: '45%', animationDelay: '0.4s' }}></div>
                  <div className="w-5 bg-gradient-to-br from-green-500 to-green-700 rounded-t-md animate-growUp" style={{ height: '90%', animationDelay: '0.6s' }}></div>
                  <div className="w-5 bg-gradient-to-br from-green-500 to-green-700 rounded-t-md animate-growUp" style={{ height: '70%', animationDelay: '0.8s' }}></div>
                  <div className="w-5 bg-gradient-to-br from-green-500 to-green-700 rounded-t-md animate-growUp" style={{ height: '85%', animationDelay: '1s' }}></div>
                  <div className="w-5 bg-gradient-to-br from-green-500 to-green-700 rounded-t-md animate-growUp" style={{ height: '55%', animationDelay: '1.2s' }}></div>
                  <div className="w-5 bg-gradient-to-br from-green-500 to-green-700 rounded-t-md animate-growUp" style={{ height: '75%', animationDelay: '1.4s' }}></div>
                </div>
                <div className="absolute top-1/2 left-8 right-8 h-0.5 bg-gradient-to-r from-green-500 via-orange-500 to-red-500 rounded-full"></div>
                <div className="absolute top-4 right-4 flex gap-2">
                  <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" style={{ animationDelay: '0s' }}></div>
                  <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" style={{ animationDelay: '0.7s' }}></div>
                  <div className="w-2 h-2 rounded-full bg-gray-500 animate-pulse" style={{ animationDelay: '1.4s' }}></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Sobre Section */}
      <section id="sobre" className="py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-8">Sobre o SinalInvest</h2>
              <p className="text-lg leading-relaxed text-gray-600 mb-6">
                Somos uma plataforma revolucionária que utiliza inteligência artificial de última geração 
                para analisar milhares de notícias financeiras em tempo real. <strong>Todas as análises são enviadas 
                diretamente no seu WhatsApp</strong>, de forma fácil e rápida, para que você não perca nenhuma oportunidade.
              </p>
              <p className="text-lg leading-relaxed text-gray-600 mb-6">
                Com mais de 5 anos de experiência em análise de mercado e tecnologia de IA, nossa equipe 
                desenvolveu algoritmos únicos que identificam padrões e tendências que passam despercebidos 
                pela maioria dos investidores. <strong>E você recebe tudo isso no WhatsApp</strong> - sem precisar acessar 
                sites ou aplicativos complexos!
              </p>
            </div>
            <div className="flex flex-col gap-8">
              <div className="text-center p-8 bg-slate-100 rounded-xl border border-gray-200 transition-transform duration-200 hover:-translate-y-1.5 hover:shadow-md">
                <div className="text-5xl font-bold text-blue-700 mb-2">50K+</div>
                <div className="text-base text-gray-600 font-medium">Notícias analisadas por dia</div>
              </div>
              <div className="text-center p-8 bg-slate-100 rounded-xl border border-gray-200 transition-transform duration-200 hover:-translate-y-1.5 hover:shadow-md">
                <div className="text-5xl font-bold text-blue-700 mb-2">95%</div>
                <div className="text-base text-gray-600 font-medium">Precisão nas previsões</div>
              </div>
              <div className="text-center p-8 bg-slate-100 rounded-xl border border-gray-200 transition-transform duration-200 hover:-translate-y-1.5 hover:shadow-md">
                <div className="text-5xl font-bold text-blue-700 mb-2">10K+</div>
                <div className="text-base text-gray-600 font-medium">Investidores ativos</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Como Funciona Section */}
      <section id="como-funciona" className="py-24 bg-slate-100">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-gray-900 mb-16 text-center">Como Funciona</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="flex flex-col items-center text-center p-8 bg-white rounded-xl shadow-sm border border-gray-200 transition-transform duration-200 hover:-translate-y-1.5 hover:shadow-md">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-700 to-blue-900 text-white flex items-center justify-center text-2xl font-bold mb-6">1</div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Coleta de Dados</h3>
                <p className="text-gray-600 leading-relaxed">Nossa IA monitora continuamente milhares de fontes de notícias, relatórios financeiros e dados de mercado.</p>
              </div>
            </div>
            <div className="flex flex-col items-center text-center p-8 bg-white rounded-xl shadow-sm border border-gray-200 transition-transform duration-200 hover:-translate-y-1.5 hover:shadow-md">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-700 to-blue-900 text-white flex items-center justify-center text-2xl font-bold mb-6">2</div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Análise Inteligente</h3>
                <p className="text-gray-600 leading-relaxed">Algoritmos avançados processam as informações, identificando sentimentos, tendências e possíveis impactos.</p>
              </div>
            </div>
            <div className="flex flex-col items-center text-center p-8 bg-white rounded-xl shadow-sm border border-gray-200 transition-transform duration-200 hover:-translate-y-1.5 hover:shadow-md">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-700 to-blue-900 text-white flex items-center justify-center text-2xl font-bold mb-6">3</div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Receba via WhatsApp</h3>
                <p className="text-gray-600 leading-relaxed">As análises da IA chegam <strong>diretamente no seu WhatsApp</strong> de forma rápida e fácil. Não precisa abrir apps ou acessar sites - tudo vem até você!</p>
              </div>
            </div>
            <div className="flex flex-col items-center text-center p-8 bg-white rounded-xl shadow-sm border border-gray-200 transition-transform duration-200 hover:-translate-y-1.5 hover:shadow-md">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-700 to-blue-900 text-white flex items-center justify-center text-2xl font-bold mb-6">4</div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Decisões Inteligentes</h3>
                <p className="text-gray-600 leading-relaxed">Com base nas análises, você toma decisões mais informadas e estratégicas para seus investimentos.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center p-8 rounded-xl bg-slate-100 transition-transform duration-300 hover:-translate-y-1.5 hover:shadow-lg">
              <div className="text-5xl mb-4">🤖</div>
              <h3 className="text-2xl font-semibold mb-4 text-gray-900">IA Avançada</h3>
              <p className="text-gray-600 leading-relaxed">Nossa inteligência artificial processa milhares de notícias em tempo real, identificando padrões e tendências do mercado.</p>
            </div>
            <div className="text-center p-8 rounded-xl bg-slate-100 transition-transform duration-300 hover:-translate-y-1.5 hover:shadow-lg">
              <div className="text-5xl mb-4">📱</div>
              <h3 className="text-2xl font-semibold mb-4 text-gray-900">Análises no WhatsApp</h3>
              <p className="text-gray-600 leading-relaxed">Receba todas as análises da IA <strong>diretamente no seu WhatsApp</strong>. Fácil, rápido e sem complicação - você recebe tudo no celular sem precisar abrir outros apps!</p>
            </div>
            <div className="text-center p-8 rounded-xl bg-slate-100 transition-transform duration-300 hover:-translate-y-1.5 hover:shadow-lg">
              <div className="text-5xl mb-4">⚡</div>
              <h3 className="text-2xl font-semibold mb-4 text-gray-900">Alertas Instantâneos</h3>
              <p className="text-gray-600 leading-relaxed">Receba notificações imediatas sobre mudanças que podem impactar suas ações, antes que o mercado reaja.</p>
            </div>
            <div className="text-center p-8 rounded-xl bg-slate-100 transition-transform duration-300 hover:-translate-y-1.5 hover:shadow-lg">
              <div className="text-5xl mb-4">🎯</div>
              <h3 className="text-2xl font-semibold mb-4 text-gray-900">Personalização Total</h3>
              <p className="text-gray-600 leading-relaxed">Configure alertas personalizados para as empresas que você acompanha e defina seus critérios de análise.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-br from-blue-900 to-blue-800 text-white text-center">
        <div className="container mx-auto px-4">
          <div>
            <h2 className="text-4xl font-bold mb-4">Pronto para transformar notícias em lucro?</h2>
            <p className="text-xl mb-8 opacity-90">Junte-se a milhares de investidores que já recebem análises inteligentes <strong>diretamente no WhatsApp</strong>. Fácil, rápido e eficiente!</p>
            <div className="flex gap-4 justify-center flex-wrap">
              <button className="px-8 py-4 text-lg font-semibold rounded-lg text-white bg-blue-700 border-2 border-blue-700 hover:bg-blue-800 transition-all duration-200" onClick={handleSignupClick}>
                Começar Agora
              </button>
              <button className="px-8 py-4 text-lg font-semibold rounded-lg text-white bg-transparent border-2 border-white hover:bg-white hover:text-blue-700 transition-all duration-200" onClick={handlePricingClick}>
                Ver Preços
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Contato Section */}
      <section id="contato" className="py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-16 items-start">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Entre em Contato</h2>
              <p className="text-lg text-gray-600 mb-12 leading-relaxed">Dúvidas? Sugestões? Estamos aqui para ajudar você a maximizar seus investimentos.</p>
              
              <div className="flex flex-col gap-8">
                <div className="flex items-center gap-4 p-6 bg-slate-100 rounded-xl border border-gray-200 transition-transform duration-200 hover:-translate-y-0.5 hover:shadow-md">
                  <div className="text-3xl w-16 h-16 rounded-full bg-blue-700 text-white flex items-center justify-center flex-shrink-0">📧</div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">Email</h3>
                    <p className="text-base text-gray-600 m-0">contato@impactaistocks.com</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-4 p-6 bg-slate-100 rounded-xl border border-gray-200 transition-transform duration-200 hover:-translate-y-0.5 hover:shadow-md">
                  <div className="text-3xl w-16 h-16 rounded-full bg-blue-700 text-white flex items-center justify-center flex-shrink-0">📱</div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">WhatsApp</h3>
                    <p className="text-base text-gray-600 m-0">+55 (11) 99999-9999</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-4 p-6 bg-slate-100 rounded-xl border border-gray-200 transition-transform duration-200 hover:-translate-y-0.5 hover:shadow-md">
                  <div className="text-3xl w-16 h-16 rounded-full bg-blue-700 text-white flex items-center justify-center flex-shrink-0">💬</div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">Chat Online</h3>
                    <p className="text-base text-gray-600 m-0">Disponível 24/7</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="bg-slate-100 p-8 rounded-xl border border-gray-200">
              <form className="flex flex-col gap-6">
                <div className="flex flex-col">
                  <input type="text" placeholder="Seu nome" className="p-4 border-2 border-gray-200 rounded-lg text-base bg-white focus:outline-none focus:border-blue-700" />
                </div>
                <div className="flex flex-col">
                  <input type="email" placeholder="Seu email" className="p-4 border-2 border-gray-200 rounded-lg text-base bg-white focus:outline-none focus:border-blue-700" />
                </div>
                <div className="flex flex-col">
                  <select className="p-4 border-2 border-gray-200 rounded-lg text-base bg-white focus:outline-none focus:border-blue-700">
                    <option>Assunto</option>
                    <option>Dúvidas sobre planos</option>
                    <option>Suporte técnico</option>
                    <option>Parcerias</option>
                    <option>Outros</option>
                  </select>
                </div>
                <div className="flex flex-col">
                  <textarea placeholder="Sua mensagem" className="p-4 border-2 border-gray-200 rounded-lg text-base bg-white focus:outline-none focus:border-blue-700 resize-y min-h-32" rows={4}></textarea>
                </div>
                <button type="submit" className="px-8 py-4 text-lg font-semibold rounded-lg text-white bg-blue-700 border-2 border-blue-700 hover:bg-blue-800 transition-all duration-200">
                  Enviar Mensagem
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white pt-12 pb-4">
        <div className="container mx-auto px-4">
          <div className="flex justify-between items-start mb-8">
            <div>
              <h3 className="text-blue-700 mb-2">SinalInvest</h3>
              <p className="opacity-80">Transformando notícias em oportunidades de investimento.</p>
            </div>
            <div className="flex gap-8">
              <a href="#sobre" className="text-white no-underline opacity-80 hover:opacity-100 transition-opacity duration-200">Sobre</a>
              <a href="#contato" className="text-white no-underline opacity-80 hover:opacity-100 transition-opacity duration-200">Contato</a>
              <a href="#privacidade" className="text-white no-underline opacity-80 hover:opacity-100 transition-opacity duration-200">Privacidade</a>
              <a href="#termos" className="text-white no-underline opacity-80 hover:opacity-100 transition-opacity duration-200">Termos</a>
            </div>
          </div>
          <div className="text-center pt-8 border-t border-white/10 opacity-60">
            <p>&copy; 2024 SinalInvest. Todos os direitos reservados.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;

